/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts';
import styled from 'styled-components';
import { UserLicenseType } from '@bb/users';
import {
  deleteUserDefaultContact,
  applyUserDefaultContact,
} from 'api/UserController';
import errorReporter from '@app/errorReporter';
import Swal from 'sweetalert2';
import {
  Title,
  Text,
  Form,
  Switch,
  List,
  ListItem,
  ListItemMeta,
  Avatar,
  Button,
  Icon,
  Row,
  Col,
  Spin,
  message,
  RadioGroup,
  Radio,
  Space,
} from 'components';
import { AgentGroup } from '@bb/domain/dist/types';
import FormContext from './FormContext';
import { UserDefaultContact } from './types';
import { Divider, StyledSwitchGrid } from './components';
import {
  ContactModalContextProvider,
  ContactModalContextProviderProps,
} from './ContactModal/ContactModalContext';
import ContactModal from './ContactModal';
import DefaultShowingSettings from './DefaultShowingSettings';
import AgentUserCalendarSync from './ListingSettingsTabComponents/AgentUserCalendarSync';

const defaultContactModalProps: ContactModalContextProviderProps = {
  visible: false,
  modeType: 'new',
  contact: null,
};

const ListingSettingsTab = () => {
  const { auth, hasFeature } = useContext(AuthContext);

  const {
    isProfile,
    isIncomingTransfer,
    contacts,
    loading,
    isNew,
    userId,
    doesUserOwnProfile,
    userState,
    setUserState,
    setContacts,
    showingSettings,
    setShowingSettings,
  } = useContext(FormContext);

  // Default by org's offerSettings
  useEffect(() => {
    setUserState(user => ({
      ...user,
      offerSettings: user.offerSettings ?? auth.org.settings.offerSettings,
    }));
  }, []);

  const {
    duplicateNotify,
    expiryReminder,
    feedbackCollected,
    feedbackNotify,
    feedbackSendToSeller,
    offerSettings,
  } = userState;

  const [contactModalProps, setContactModalProps] =
    useState<ContactModalContextProviderProps>(defaultContactModalProps);

  if (loading) return <Spin size="large" />;

  const deleteContactAlert = async (contact: UserDefaultContact) => {
    const isDeleteConfirm = await Swal.fire({
      title: 'Delete Contact',
      text: `Are you sure you want to delete ${contact.firstName} ${contact.lastName}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Delete',
    });

    if (isDeleteConfirm.value) {
      try {
        await deleteUserDefaultContact({
          userId,
          contactId: contact.contactId,
        });
        setContacts(
          contacts.filter(({ contactId }) => contactId !== contact.contactId),
        );
        message.success(`Contact deleted successfully`);
        await applyDeleteContactAlert(contact.contactId);
      } catch (err) {
        message.error('Error deleting user');
        errorReporter.error(err);
      }
    }
  };

  const applyDeleteContactAlert = async (contactId: string) => {
    const isApplyConfirm = await Swal.fire({
      title: 'Delete Contact',
      text: `Apply this change to all existing listings?`,
      type: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3ea800',
      confirmButtonText: 'Apply',
    });
    if (isApplyConfirm.value) {
      try {
        const applySettings = {
          userId,
          isDelete: true,
          contactId,
        };
        await applyUserDefaultContact(applySettings);
        message.success(`Listings updated successfully`);
      } catch (err) {
        errorReporter.error(`Failed to apply changes: ${err}`);
      }
    }
  };

  const handleAddContact = () =>
    setContactModalProps({ visible: true, modeType: 'new', contact: null });
  const handleEditContact = (contact: UserDefaultContact) =>
    setContactModalProps({ visible: true, modeType: 'edit', contact });

  return (
    <>
      <Form layout="vertical">
        <Text>
          The following settings will be applied by default for all of your new
          listings. (You can change these settings on a per listing basis.)
        </Text>

        <Title level={3} hideMargin>
          Feedback Collection
        </Title>

        <StyledSwitchGrid>
          <Switch
            checked={feedbackCollected}
            onChange={checked =>
              setUserState(cur => ({ ...cur, feedbackCollected: checked }))
            }
            data-cy="settings-toggle-collect-feedback"
          />{' '}
          Collect automated feedback on all of my listings
          <Switch
            checked={feedbackNotify}
            onChange={checked =>
              setUserState(cur => ({ ...cur, feedbackNotify: checked }))
            }
            data-cy="settings-toggle-notify-feedback-la"
          />{' '}
          Notify me when new feedback is collected on my listings
          <Switch
            checked={feedbackSendToSeller}
            onChange={checked =>
              setUserState(cur => ({
                ...cur,
                feedbackSendToSeller: checked,
              }))
            }
            data-cy="settings-toggle-notify-feedback-seller"
          />{' '}
          Automatically send feedback to sellers by default for active (existing
          and new) listings
        </StyledSwitchGrid>

        <Divider />
        <Title level={3}>Automatic Merge Suggestion</Title>
        <StyledSwitchGrid>
          <Switch
            checked={duplicateNotify}
            onChange={checked =>
              setUserState(cur => ({ ...cur, duplicateNotify: checked }))
            }
          />
          Email me about automatic merge suggestions for my listings
        </StyledSwitchGrid>
        <Divider />

        <Title level={3}>Expiry Notification</Title>

        <StyledSwitchGrid>
          <Switch
            checked={expiryReminder}
            onChange={checked =>
              setUserState(cur => ({ ...cur, expiryReminder: checked }))
            }
          />{' '}
          Email me when my listing is going to be expired in 7 days
        </StyledSwitchGrid>

        {hasFeature('OFFERS') && (
          <>
            <Divider />

            <Title level={3} hideMargin>
              Offer Notification Settings
            </Title>

            <SubTitle>
              This setting will determine who will be notified when an offer is
              registered:
            </SubTitle>

            <RadioGroup
              value={offerSettings?.agentGroup}
              onChange={e =>
                setUserState(user => ({
                  ...user,
                  offerSettings: {
                    ...offerSettings,
                    agentGroup: e.target.value,
                  },
                }))
              }
            >
              <Space direction="vertical">
                <Radio
                  data-cy="listing-settings-auto-notify-switch"
                  value={'all' as AgentGroup}
                >
                  <Space direction="vertical">
                    <RadioText>Auto Notify All Showing Agents</RadioText>
                    <RadioSubText>
                      All Showing agents will be instantly notified
                    </RadioSubText>
                  </Space>
                </Radio>
                <Radio
                  data-cy="listing-settings-notify-all-agents-switch"
                  value={'none' as AgentGroup}
                >
                  <Space direction="vertical">
                    <RadioText>Notify Listing Agents ONLY</RadioText>
                    <RadioSubText>
                      A notification wil be sent to the listing agent(s) only.
                      They will decide when and whom to notify
                    </RadioSubText>
                  </Space>
                </Radio>
              </Space>
            </RadioGroup>

            <StyledSwitchGrid>
              <Switch
                data-cy="listing-settings-include-total-number-switch"
                checked={offerSettings?.shouldShowOfferCount}
                onChange={checked =>
                  setUserState(user => ({
                    ...user,
                    offerSettings: {
                      ...user.offerSettings,
                      shouldShowOfferCount: checked,
                    },
                  }))
                }
              />{' '}
              Include the total # of registered offers in showing confirmation
            </StyledSwitchGrid>
          </>
        )}

        {!(isNew || isIncomingTransfer) && (
          <>
            <Divider />

            <Title level={3}>Showing Configurations</Title>

            {doesUserOwnProfile && (
              <DefaultShowingSettings
                showingSettings={showingSettings}
                setShowingSettings={setShowingSettings}
              />
            )}

            <Text type="secondary">
              Add the following users to all of my listing configurations by
              default:
            </Text>

            <List
              footer={
                <Button
                  data-cy="listing-settings-add-contact"
                  type="dashed"
                  block
                  icon={<StyledIcon type="plus" offset={[0, 1]} />}
                  onClick={handleAddContact}
                >
                  Add Contact
                </Button>
              }
            >
              {contacts.map(user => (
                <ListItem
                  data-cy={`listing-settings-user-contact-${user.contactId}`}
                  key={user.contactId}
                  actions={[
                    <Row key="actions" align="middle" gutter={16}>
                      <Col>
                        <Button
                          type="link"
                          onClick={() => handleEditContact(user)}
                        >
                          Edit
                        </Button>
                      </Col>
                      <Col>
                        <StyledXButton
                          type="primary"
                          shape="circle"
                          icon={
                            <StyledIcon type="x" width="12px" height="12px" />
                          }
                          onClick={() => deleteContactAlert(user)}
                        />
                      </Col>
                    </Row>,
                  ]}
                >
                  <ListItemMeta
                    avatar={<Avatar user={user} />}
                    title={
                      <Text block>
                        {user.firstName} {user.lastName}
                      </Text>
                    }
                    description={
                      (auth.org.locations[Number(user.location)] as any).name
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {auth.licenseType === UserLicenseType.LICENSED &&
          doesUserOwnProfile && (
            <>
              <Divider />
              <AgentUserCalendarSync />
            </>
          )}
      </Form>

      <ContactModalContextProvider {...contactModalProps}>
        <ContactModal
          onClose={() => setContactModalProps(defaultContactModalProps)}
        />
      </ContactModalContextProvider>
    </>
  );
};

export default ListingSettingsTab;

const SubTitle = styled.span`
  color: #808080;
  display: block;
  margin-top: 4px;
  margin-bottom: 24px;
`;

const RadioText = styled.span``;

const RadioSubText = styled.span`
  color: #a0a0a0;
  font-size: 12px;
`;

const StyledIcon = styled(Icon)`
  && {
    color: inherit;
    transition: none;
  }
`;

const StyledXButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
`;
